import { Component, inject } from "@angular/core";
import {
    EffectivenessSourceDocumentsModel,
    WorkflowGenerateSamplesModel,
} from "../../../shared/models";
import { AbstractEffTestingStatusComponent } from "../abstract-eff-testing-status-component";
import { CerrixPromptComponent } from "@app/shared/cerrix-prompt/cerrix-prompt.component";
import { DocumentManagerPermissionModel } from "@models/moi/MoiDocumentPermissionModel";
import { EffectivenessStatusType, SampleGenerationType, SampleType } from "../../../shared/enums";
import { ApplicationSettings } from "@services/http/settings/application-settings";
import { firstValueFrom } from "rxjs";
import { SettingsDataService } from "@services/http/SettingsDataService";
import { PopupActionResult } from "../../common/popups/models/popup-action-result.model";
import { GenerateSamplesPopupComponent } from "../popups/generate-samples-popup/generate-samples-popup.component";
import { DocumentModel } from "@models/documents/documentmodel";
import { ControlExecutionOverviewModel } from "@app/controls/models/control-execution/control-execution-overview-model";
import { EffectivenessTaskCounts } from "../../../shared/models/effectiveness-taskcounts";
import { GenericPaginationListConfig } from "@app/shared/models/GenericPaginationList/GenericPaginationListConfig";
import { FormatType } from "@enums/FormatType";
import { nameof } from "@methods/jeffs-toolkit";
import { provideTranslocoScope, TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { EffStepHeaderComponent } from "../../common/eff-step-header/eff-step-header.component";
import { DocumentManagerNewComponent } from "../../common/document-manager/document-manager.component";
import { EffControlTaskCountInfoBoxComponent } from "../../info-boxes/eff-control-task-count-info-box/eff-control-task-count-info-box.component";
import { EffButtonComponent } from "../../common/eff-button/eff-button.component";
import { SharedModule } from "@app/shared/shared.module";
import { CerrixSlideOverComponent } from "@app/shared/cerrix-slide-over/cerrix-slide-over.component";
import { EffExpansionPanelComponent } from "../../common/eff-expansion-panel/eff-expansion-panel.component";
import { EffEvidenceInstructionsInfoBoxComponent } from "../../info-boxes/eff-evidence-instructions-info-box/eff-evidence-instructions-info-box.component";
import { EffTestInstructionsInfoBoxComponent } from "../../info-boxes/eff-test-instructions-info-box/eff-test-instructions-info-box.component";
import { EffControlInfoBoxComponent } from "../../info-boxes/eff-control-info-box/eff-control-info-box.component";

@Component({
    selector: "eff-testing-upload-source-document",
    templateUrl: "./eff-testing-upload-source-document.component.html",
    styleUrls: ["./eff-testing-upload-source-document.component.scss"],
    providers: [provideTranslocoScope("controls")],
    imports: [
        TranslocoPipe,
        EffStepHeaderComponent,
        DocumentManagerNewComponent,
        EffControlTaskCountInfoBoxComponent,
        EffButtonComponent,
        SharedModule,
        CerrixSlideOverComponent,
        EffExpansionPanelComponent,
        EffEvidenceInstructionsInfoBoxComponent,
        EffTestInstructionsInfoBoxComponent,
        EffControlInfoBoxComponent,
    ]
})
export class EffTestingUploadSourceDocumentComponent extends AbstractEffTestingStatusComponent {
    private _transloco = inject(TranslocoService);
    sourceDocumentModel: EffectivenessSourceDocumentsModel;
    sourceDocumentPermissions = new DocumentManagerPermissionModel();
    executionData: ControlExecutionOverviewModel[] = [];
    executionConfig: GenericPaginationListConfig;

    prompt: CerrixPromptComponent;
    samplingInformation: WorkflowGenerateSamplesModel;
    taskCounts = new EffectivenessTaskCounts();

    buttonWrapperClassString: string = "bottom-buttons-wrapper";
    controlDidNotOccurButtonClassString: string = "bottom-button control-not-occured-btn";
    showControlDidntOccurButton: boolean;
    showGenerateSamplesControlExecutionButton: boolean;

    onLoad(): void {
        this._ds.getSourceDocuments(this.effectivenessModel.guid).subscribe((data) => {
            this.sourceDocumentModel = data;
        });

        // Bypassing the regular permissions if the status equals "waitingForSourceDocuments" or "waitingForSamples"
        // This is specifically done to keep the "old way" intact
        if (this.stepPermissions.status < 0) {
            this.stepPermissions.sourceDocumentUploadAllowed = true;
            this.stepPermissions.sampleGenerationAllowed = true;
        }
        this.sourceDocumentPermissions.canAddDocument =
            this.stepPermissions.sourceDocumentUploadAllowed;

        this._ds.getExecutionOverview(this.effectivenessModel.guid).subscribe((data) => {
            this.executionData = data;
            this.calculateCounts();
            this.calculateDidNotOccurButtonClasses();
            this.showControlDidntOccurButton =
                this.stepPermissions.sourceDocumentUploadAllowed &&
                (!this.isUseControlExecution() ||
                    (this.executionData && this.executionData.length == 0));
            this.showGenerateSamplesControlExecutionButton =
                this.stepPermissions.isCompleteAllowed &&
                this.isUseControlExecution() &&
                this.executionData &&
                this.executionData.length > 0;
            this.createExecutionConfig();
        });
    }

    private calculateCounts() {
        if (this.executionData) {
            var population = 0;
            var completed = 0;
            var notCompleted = 0;

            for (var model of this.executionData) {
                population++;
                if (model.completedAt) {
                    completed++;
                } else {
                    notCompleted++;
                }
            }
            this.taskCounts.populationSize = population;
            this.taskCounts.completed = completed;
            this.taskCounts.notCompleted = notCompleted;
        }
    }

    private calculateDidNotOccurButtonClasses() {
        if (this.isUseControlExecution() && this.taskCounts.populationSize == 0) {
            this.buttonWrapperClassString += " left-align";
            this.controlDidNotOccurButtonClassString = "bottom-button without-source-documents-btn";
        }
    }

    private async controlDidNotOccur() {
        await this.saveSourceDocuments(true);
        await this.executeWorkflow(
            this._workflowDs.postWorkflowControlDidNotOccur(this.effectivenessModel.guid),
        );
    }

    async controlDidNotOccurWithConfirmation() {
        var result = await this._dialogService.showConfirmationDialog(
            this._transloco.translate("controls.effTestingUploadSourceDocument.controlDidntOccurTitle"),
            this._transloco.translate("controls.effTestingUploadSourceDocument.controlDidntOccurMessage"),
            this._transloco.translate("controls.effTestingUploadSourceDocument.controlDidntOccurButtonLabel"),
            this._transloco.translate("controls.effTestingUploadSourceDocument.cancelButton"),
            "820px",
            "warning-button",
        );

        if (result == PopupActionResult.OK) {
            await this.controlDidNotOccur();
            this.cerrixTab.refresh();
        }
    }

    areDocumentsUploaded() {
        return this.visibleDocuments(this.sourceDocumentModel.documents).length > 0;
    }

    isUseControlExecution() {
        return this.effectivenessModel.useControlExecution;
    }

    isDocumentSelected() {
        return (
            this.visibleDocuments(this.sourceDocumentModel.documents).length == 1 ||
            this.visibleDocuments(this.sourceDocumentModel.documents).filter(
                (doc) => doc.IsSelected,
            ).length > 0
        );
    }

    async completeSourceDocuments(skipPageReload: boolean = false) {
        let visibleDocuments = this.visibleDocuments(this.sourceDocumentModel.documents);
        var sourceDocuments: EffectivenessSourceDocumentsModel = {
            idColumn: this.samplingInformation.idColumn,
            rowsToSkip: this.samplingInformation.rowsToSkip,
            documents: visibleDocuments,
        };

        await this.executeWorkflow(
            this._workflowDs.postWorkflowUploadSourceDocuments(
                this.effectivenessModel.guid,
                sourceDocuments,
            ),
            skipPageReload,
        );
    }

    async saveSourceDocuments(skipPageReload: boolean = false) {
        if (
            this.sourceDocumentModel.documents.every(
                (sd) => !sd.IsNew && !sd.Changed && !sd.Deleted,
            )
        ) {
            return;
        }
        await this.executeWorkflow(
            this._ds.postSourceDocuments(this.effectivenessModel.guid, this.sourceDocumentModel),
            skipPageReload,
        );

        this._saveButtonService.triggerSaveSuccess();
    }

    documentDeleted(document: DocumentModel) {
        if (!document.IsNew) {
            this.saveSourceDocuments();
        }
    }

    async completeSourceDocumentsWithConfirm() {
        var result = await this._dialogService.showConfirmationDialog(
            this._transloco.translate("controls.effTestingUploadSourceDocument.noSourceDocumentsTitle"),
            this._transloco.translate("controls.effTestingUploadSourceDocument.noSourceDocumentsMessage"),
            this._transloco.translate("controls.effTestingUploadSourceDocument.startGeneratingSamples"),
            this._transloco.translate("controls.effTestingUploadSourceDocument.cancelButton"),
            "820px",
        );

        if (result == PopupActionResult.OK) {
            this.openGenerateSamplePopup();
        }
    }

    async openGenerateSamplePopup() {
        await this.saveSourceDocuments(true);

        var newSourceDocumentModel = await firstValueFrom(
            this._ds.getSourceDocuments(this.effectivenessModel.guid),
        );

        newSourceDocumentModel.documents.forEach((newDocument) => {
            let savedDocument = this.sourceDocumentModel.documents.find(
                (doc) => doc.Name == newDocument.Name,
            );
            if (savedDocument) {
                newDocument.IsSelected = savedDocument.IsSelected;
            }
        });

        this.sourceDocumentModel = newSourceDocumentModel;

        let availableSampleTypes = await this.getAvailableSampleTypes();

        var populationSize = 1;
        if (this.isUseControlExecution()) {
            populationSize = this.taskCounts.populationSize;
        }

        this._dialog.open(GenerateSamplesPopupComponent, {
            data: {
                title: this._transloco.translate("controls.effTestingUploadSourceDocument.generateSamplesTitle"),
                effectivenessModel: this.effectivenessModel,
                sourceDocumentModel: this.sourceDocumentModel,
                availableSampleTypes: availableSampleTypes,
                populationSize: populationSize,
                save: async function (samplingInformation: WorkflowGenerateSamplesModel) {
                    this.samplingInformation = samplingInformation;
                    await this.postSampleConfiguration();
                }.bind(this),
            },
            panelClass: "eff-popup-wrapper",
            width: "825px",
        });
    }

    private async executeWorkflow(workflowFunction, skipPageReload: boolean = false) {
        const prompt = this._promptService.loader(this._transloco.translate("controls.effTestingUploadSourceDocument.savingSourceDocumentsLoader"));
        try {
            await firstValueFrom(workflowFunction);
            if (!skipPageReload) {
                this.onLoad();
            }
        } finally {
            prompt.close();
        }
    }

    private async postSampleConfiguration() {
        if (this.stepPermissions.status != EffectivenessStatusType.waitingForSamples) {
            await this.completeSourceDocuments(true);
            this.stepPermissions.status = EffectivenessStatusType.waitingForSamples;
        }
        await firstValueFrom(
            this._workflowDs.postWorkflowGenerateSamples(
                this.effectivenessModel.guid,
                this.samplingInformation,
            ),
        ).then(() => {
            this._dialog.closeAll();
            this.cerrixTab.refresh();
        });
    }

    private async getAvailableSampleTypes(): Promise<any[]> {
        const settingService = this._injector.get(SettingsDataService);
        const setting = (
            await firstValueFrom(settingService.getSetting(ApplicationSettings.GenerateSamplesType))
        ).TextValue;

        let sampleTypeOptions = [];
        const showSpreadsheetGeneration =
            this.effectivenessModel.sampleGenerationType == SampleGenerationType.automatic &&
            this.sourceDocumentModel &&
            this.sourceDocumentModel.documents &&
            this.visibleDocuments(this.sourceDocumentModel.documents).length > 0;

        const options = setting.split(";");
        options.forEach((option) => {
            if (option.startsWith("[") && option.endsWith("]")) {
                switch (option.toLowerCase()) {
                    case "[all]":
                        sampleTypeOptions = [
                            { ID: SampleType.number, Name: "Number" },
                            { ID: SampleType.date, Name: "Date" },
                        ];
                        if (
                            this.effectivenessModel.sampleGenerationType ==
                            SampleGenerationType.manual
                        ) {
                            sampleTypeOptions.push({
                                ID: SampleType.text,
                                Name: "Text",
                            });
                        }

                        if (showSpreadsheetGeneration) {
                            sampleTypeOptions.push({
                                ID: SampleType.spreadsheet,
                                Name: "Spreadsheet",
                            });
                        }

                        break;
                    case "[number]":
                        sampleTypeOptions = [{ ID: SampleType.number, Name: "Number" }];
                        break;
                    case "[date]":
                        sampleTypeOptions = [{ ID: SampleType.date, Name: "Date" }];
                        break;
                    case "[text]":
                        sampleTypeOptions = [{ ID: SampleType.text, Name: "Text" }];
                        break;
                    case "[spreadsheet]":
                        if (showSpreadsheetGeneration) {
                            sampleTypeOptions = [
                                { id: SampleType.spreadsheet, Name: "Spreadsheet" },
                            ];
                        } else {
                            sampleTypeOptions = [{ ID: SampleType.number, Name: "Number" }];
                        }
                        break;
                }
            }
        });

        return sampleTypeOptions;
    }

    private createExecutionConfig() {
        if (this.executionConfig) {
            return;
        }

        this.executionConfig = <GenericPaginationListConfig>{
            name: "Control Execution population",
            idProp: nameof<ControlExecutionOverviewModel>((x) => x.guid),
            sortProp: nameof<ControlExecutionOverviewModel>((x) => x.actionDate),
            overviewSortBy: nameof<ControlExecutionOverviewModel>((x) => x.actionDate),
            uiid: "control-execution-population-overview",
            isSortableByColumn: true,

            allowEdit: true,

            fields: [
                {
                    fieldName: nameof<ControlExecutionOverviewModel>((x) => x.identifier),
                    prettyName: "ID",
                    headerCssClass: "th-fixed-short",
                },
                {
                    fieldName: nameof<ControlExecutionOverviewModel>((x) => x.name),
                    prettyName: "Name",
                    headerCssClass: "th-fixed-default",
                },
                {
                    fieldName: nameof<ControlExecutionOverviewModel>((x) => x.statusDescription),
                    prettyName: "Status",
                    headerCssClass: "th-max-short",
                },
                {
                    fieldName: nameof<ControlExecutionOverviewModel>((x) => x.scoreName),
                    prettyName: "Score",
                    headerCssClass: "th-max-default",
                },
                {
                    fieldName: nameof<ControlExecutionOverviewModel>((x) => x.actionDate),
                    prettyName: "Action date",
                    formatType: FormatType.DateFormat,
                    headerCssClass: "th-max-short",
                },
                {
                    fieldName: nameof<ControlExecutionOverviewModel>((x) => x.completedAt),
                    prettyName: "Completed",
                    formatType: FormatType.DateFormat,
                    headerCssClass: "th-max-short",
                },
            ],

            data: this.executionData,
            editOverride: (row) => {
                this._tabService.generateTab(this._pages.TaskDetail, row.guid, "Control Execution");
            },
            setAdditionalColumns: (row) => {
                for (const item of row) {
                    switch (item.statusDescription) {
                        case "To do":
                            item.statusDescription_ICON = "fa-circle-xmark";
                            item.statusDescription_COLOR_CLASS = "status-error";
                            break;
                        case "Ready for Review":
                        case "Rejected":
                            item.statusDescription_ICON = "fa-circle-minus";
                            item.statusDescription_COLOR_CLASS = "status-attention";
                            break;
                        case "Done":
                            item.statusDescription_ICON = "fa-circle-check";
                            item.statusDescription_COLOR_CLASS = "status-success";
                            break;
                    }

                    if (!item.scoreName || item.scoreName.length === 0) {
                        item.scoreName = "No score";
                        item.scoreName_COLOR = null;
                    }
                }
            },
        };
    }
}
